

import React, { useState, useEffect, RefObject } from 'react';

// Reusable hook for clicking outside an element
export const useOnClickOutside = (ref: RefObject<HTMLElement | null>, handler: (event: MouseEvent | TouchEvent) => void) => {
    useEffect(() => {
        const listener = (event: MouseEvent | TouchEvent) => {
            // Do nothing if clicking ref's element or descendent elements
            if (!ref.current || ref.current.contains(event.target as Node)) {
                return;
            }
            handler(event);
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler]);
};


// Reusable hook for clipboard functionality
export const useCopyToClipboard = (): [(text: string) => void, boolean] => {
  const [isCopied, setIsCopied] = useState(false);

  const copy = (text: string) => {
    if (isCopied) return;
    
    navigator.clipboard.writeText(text).then(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }).catch(err => {
        console.error('Failed to copy text: ', err);
    });
  };

  return [copy, isCopied];
};

// Reusable component for Google Material Symbols
export const Icon: React.FC<{ children: string; className?: string }> = ({ children, className }) => {
  // Check if Material Symbols font is loaded
  const isFontLoaded = document.fonts?.check('1em "Material Symbols Outlined"') || 
                      document.fonts?.check('1em Material Symbols Outlined') ||
                      true; // Fallback to true for now
  
  if (!isFontLoaded) {
    console.warn('Material Symbols font not loaded. Icons may not display correctly.');
  }
  
  return (
    <span 
      className={`material-symbols-outlined inline-flex items-center justify-center ${className || ''}`}
      style={{ 
        fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24",
        fontSize: '1.2em',
        lineHeight: '1'
      }}
      title={children} // Add tooltip for accessibility
    >
      {children}
    </span>
  );
};